import React, {
    useEffect,
    useState,
    useMemo
} from 'react';
import { useNavigate } from 'react-router-dom';

import axios from 'axios';

import {
    FiEdit2,
    FiTrash2,
    FiPlus,
    FiSearch,
    FiEye,
    FiEyeOff,
    FiFilter,
    FiX
} from 'react-icons/fi';

import './HrTeams.css';
import toast from 'react-hot-toast';

const api = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

/* ── blank filter state ── */
const EMPTY_FILTERS = { role: '', status: '', department: '' };

const HrTeams = () => {

    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    /* filter panel */
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [pendingFilters, setPendingFilters] = useState(EMPTY_FILTERS);

    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const navigate = useNavigate();

    const [showEditModal, setShowEditModal] = useState(false);
    const [editEmployee, setEditEmployee] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [editSaving, setEditSaving] = useState(false);
    const [showEditPassword, setShowEditPassword] = useState(false);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [showProfileModal, setShowProfileModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [employeeCertificates, setEmployeeCertificates] = useState([]);

    const [formData, setFormData] = useState({
        username: '', password: '', email: '', role: 'USER', employeeId: '', attendanceId: '',
        firstName: '', middleName: '', lastName: '',
        dateOfBirth: '', gender: 'MALE',
        phoneNumber: '', alternatePhone: '',
        addressLine1: '', addressLine2: '',
        city: '', state: '', postalCode: '', country: '',
        hireDate: '', jobTitle: '', department: '',
        managerId: '', employmentType: 'FULL_TIME', workLocation: '',
        salary: '', bankAccountNumber: '', bankName: '', ifscCode: '',
        emergencyContactName: '', emergencyContactPhone: '', emergencyContactRelationship: ''
    });

    /* ── fetch ── */
    const fetchEmployees = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await api.get('/admin/users');
            setEmployees(response.data);
        } catch (err) {
            console.error(err);
            setError(err?.response?.data?.message || 'Failed to load employees. Check your permissions.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchEmployees(); }, []);

    /* ── derive unique values for filter dropdowns ── */
    const uniqueRoles = useMemo(() =>
        [...new Set(employees.map(e => e.role).filter(Boolean))].sort()
        , [employees]);

    const uniqueDepartments = useMemo(() =>
        [...new Set(employees.map(e => e.department).filter(Boolean))].sort()
        , [employees]);

    /* ── count active filters for badge ── */
    const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

    /* ── combined search + filter ── */
    const filteredEmployees = useMemo(() => {
        return employees.filter((emp) => {
            const term = searchTerm.toLowerCase();
            const fullName = [emp.firstName, emp.middleName, emp.lastName]
                .filter(Boolean).join(' ').toLowerCase();

            const matchesSearch = !term || (
                emp.employeeId?.toLowerCase().includes(term) ||
                fullName.includes(term) ||
                emp.email?.toLowerCase().includes(term) ||
                emp.department?.toLowerCase().includes(term) ||
                emp.jobTitle?.toLowerCase().includes(term) ||
                emp.phoneNumber?.toLowerCase().includes(term) ||
                emp.role?.toLowerCase().includes(term)
            );

            const matchesRole = !filters.role || emp.role === filters.role;
            const matchesStatus = !filters.status ||
                (filters.status === 'active' && emp.isActive === true) ||
                (filters.status === 'inactive' && emp.isActive === false);
            const matchesDept = !filters.department || emp.department === filters.department;

            return matchesSearch && matchesRole && matchesStatus && matchesDept;
        });
    }, [employees, searchTerm, filters]);

    /* ── filter panel handlers ── */
    const handlePendingFilterChange = (e) => {
        const { name, value } = e.target;
        setPendingFilters(prev => ({ ...prev, [name]: value }));
    };

    const applyFilters = () => {
        setFilters(pendingFilters);
        setShowFilterPanel(false);
    };

    const clearFilters = () => {
        setPendingFilters(EMPTY_FILTERS);
        setFilters(EMPTY_FILTERS);
        setShowFilterPanel(false);
    };

    const openFilterPanel = () => {
        setPendingFilters(filters); // pre-fill with currently applied filters
        setShowFilterPanel(true);
    };

    /* ── view profile ── */
    const handleViewProfile = async (employee) => {
        try {
            setSelectedEmployee(employee);
            setShowProfileModal(true);
            const response = await api.get(`/api/certificate/employee/${employee.employeeId}`);
            setEmployeeCertificates(response.data || []);
        } catch (err) {
            console.error(err);
            setEmployeeCertificates([]);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    /* ── register ── */
    const handleRegister = async () => {
        try {
            const payload = {
                ...formData,
                managerId: formData.managerId ? Number(formData.managerId) : null,
                salary: formData.salary ? Number(formData.salary) : 0
            };
            const response = await api.post('/auth/signup', payload);
            toast.success(typeof response.data === 'string' ? response.data : 'Employee registered successfully');
            const newEmployee = {
                id: employees.length + 1,
                employeeId: formData.employeeId,
                attendanceId: formData.attendanceId || null,
                firstName: formData.firstName,
                middleName: formData.middleName,
                lastName: formData.lastName,
                role: formData.role,
                email: formData.email
            };
            setEmployees([...employees, newEmployee]);
            setShowRegisterModal(false);
        } catch (err) {
            console.error('FULL REGISTER ERROR:', err);
            if (err.response) {
                toast.error(typeof err.response.data === 'string' ? err.response.data : JSON.stringify(err.response.data));
            } else if (err.request) {
                toast.error('Server did not respond');
            } else {
                toast.error(err.message);
            }
        }
    };

    /* ── edit ── */
    const handleEditClick = (employee) => {
        setEditEmployee(employee);
        setEditFormData({
            username: employee.username || '',
            password: '',
            email: employee.email || '',
            role: employee.role || 'USER',
            attendanceId: employee.attendanceId || '',
            firstName: employee.firstName || '',
            middleName: employee.middleName || '',
            lastName: employee.lastName || '',
            dateOfBirth: employee.dateOfBirth ? String(employee.dateOfBirth).slice(0, 10) : '',
            gender: employee.gender || 'MALE',
            phoneNumber: employee.phoneNumber || '',
            alternatePhone: employee.alternatePhone || '',
            addressLine1: employee.addressLine1 || '',
            addressLine2: employee.addressLine2 || '',
            city: employee.city || '',
            state: employee.state || '',
            postalCode: employee.postalCode || '',
            country: employee.country || '',
            hireDate: employee.hireDate ? String(employee.hireDate).slice(0, 10) : '',
            jobTitle: employee.jobTitle || '',
            department: employee.department || '',
            managerId: employee.managerId || '',
            employmentType: employee.employmentType || 'FULL_TIME',
            workLocation: employee.workLocation || '',
            salary: employee.salary || '',
            bankAccountNumber: employee.bankAccountNumber || '',
            bankName: employee.bankName || '',
            ifscCode: employee.ifscCode || '',
            emergencyContactName: employee.emergencyContactName || '',
            emergencyContactPhone: employee.emergencyContactPhone || '',
            emergencyContactRelationship: employee.emergencyContactRelationship || '',
            isActive: employee.isActive ?? true,
        });
        setShowEditPassword(false);
        setShowEditModal(true);
    };

    const handleEditInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleUpdate = async () => {
        if (!editEmployee) return;
        setEditSaving(true);
        try {
            const payload = {
                username: editEmployee.username,
                password: editFormData.password || 'UNCHANGED_PASSWORD_PLACEHOLDER',
                email: editFormData.email,
                employeeId: editEmployee.employeeId,
                attendanceId: editFormData.attendanceId || null,
                firstName: editFormData.firstName,
                middleName: editFormData.middleName || null,
                lastName: editFormData.lastName,
                dateOfBirth: editFormData.dateOfBirth || null,
                gender: editFormData.gender || null,
                phoneNumber: editFormData.phoneNumber?.replace(/\D/g, '').slice(0, 10) || null,
                alternatePhone: editFormData.alternatePhone || null,
                addressLine1: editFormData.addressLine1 || null,
                addressLine2: editFormData.addressLine2 || null,
                city: editFormData.city || null,
                state: editFormData.state || null,
                postalCode: editFormData.postalCode || null,
                country: editFormData.country || null,
                jobTitle: editFormData.jobTitle || null,
                department: editFormData.department || null,
                hireDate: editFormData.hireDate || null,
                managerId: editFormData.managerId ? Number(editFormData.managerId) : null,
                employmentType: editFormData.employmentType || null,
                workLocation: editFormData.workLocation || null,
                salary: editFormData.salary ? Number(editFormData.salary) : null,
                bankName: editFormData.bankName || null,
                bankAccountNumber: editFormData.bankAccountNumber || null,
                ifscCode: editFormData.ifscCode || null,
                emergencyContactName: editFormData.emergencyContactName || null,
                emergencyContactPhone: editFormData.emergencyContactPhone || null,
                emergencyContactRelationship: editFormData.emergencyContactRelationship || null,
                isActive: editFormData.isActive,
                role: editFormData.role,
            };
            await api.put(`/admin/users/${editEmployee.id}`, payload);
            setShowEditModal(false);
            setEditEmployee(null);
            // Refetch from server so the table always reflects the actual saved data
            await fetchEmployees();
            toast.success('User updated successfully');
        } catch (err) {
            console.error('Update error:', err);
            const msg = err?.response?.data;
            if (msg && typeof msg === 'object') {
                const errors = Object.entries(msg).map(([k, v]) => k + ': ' + v).join('\n');
                toast.error('Validation errors: ' + errors);
            } else {
                toast.error(typeof msg === 'string' ? msg : err.message || 'Update failed.');
            }
        } finally {
            setEditSaving(false);
        }
    };

    /* ── delete ── */
    const handleDeleteClick = (employee) => { setDeleteTarget(employee); };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            await api.delete(`/admin/users/${deleteTarget.id}`);
            setEmployees(prev => prev.filter(emp => emp.id !== deleteTarget.id));
            setDeleteTarget(null);
        } catch (err) {
            console.error('Delete error:', err);
            const msg = err?.response?.data;
            toast.error(typeof msg === 'string' ? msg : err.message || 'Delete failed.');
        } finally {
            setDeleteLoading(false);
        }
    };

    /* ── tooltip mouse tracking ── */
    useEffect(() => {
        const handleMouseMove = (e) => {
            document.documentElement.style.setProperty('--tt-x', e.clientX + 'px');
            document.documentElement.style.setProperty('--tt-y', e.clientY + 'px');
        };
        document.addEventListener('mousemove', handleMouseMove);
        return () => document.removeEventListener('mousemove', handleMouseMove);
    }, []);

    /* ══════════════════════════════════════════════════════════
       RENDER
    ══════════════════════════════════════════════════════════ */
    return (
        <div className="hr-content animate-fade-in">

            <div className="teams-container">

                {/* ── Top bar ── */}
                <div className="teams-top-bar">

                    <div className="teams-search-row">

                        <button
                            className="teams-back-btn"
                            onClick={() => navigate('/EmpDashboard')}
                        >
                            ←
                        </button>

                        <div className="teams-search">
                            <FiSearch className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search employee..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {/* Filter button with active badge */}
                        <div className="teams-filter-wrap">
                            <button
                                className={`filter-btn${activeFilterCount > 0 ? ' filter-btn--active' : ''}`}
                                title="Filter"
                                onClick={openFilterPanel}
                            >
                                <FiFilter size={16} />
                                {activeFilterCount > 0 && (
                                    <span className="filter-badge">{activeFilterCount}</span>
                                )}
                            </button>
                        </div>

                    </div>

                    <button
                        className="register-btn"
                        onClick={() => setShowRegisterModal(true)}
                    >
                        <FiPlus /> Register
                    </button>

                </div>

                {/* ── Active filter chips ── */}
                {activeFilterCount > 0 && (
                    <div className="teams-filter-chips">
                        {filters.role && (
                            <span className="filter-chip">
                                Role: {filters.role}
                                <button onClick={() => setFilters(f => ({ ...f, role: '' }))}>
                                    <FiX size={11} />
                                </button>
                            </span>
                        )}
                        {filters.status && (
                            <span className="filter-chip">
                                Status: {filters.status === 'active' ? 'Active' : 'Inactive'}
                                <button onClick={() => setFilters(f => ({ ...f, status: '' }))}>
                                    <FiX size={11} />
                                </button>
                            </span>
                        )}
                        {filters.department && (
                            <span className="filter-chip">
                                Dept: {filters.department}
                                <button onClick={() => setFilters(f => ({ ...f, department: '' }))}>
                                    <FiX size={11} />
                                </button>
                            </span>
                        )}
                        <button className="filter-chip filter-chip--clear" onClick={clearFilters}>
                            Clear all
                        </button>
                    </div>
                )}

                {/* ── Table ── */}
                <div className="teams-table-wrapper">
                    {loading ? (
                        <div className="teams-message">Loading employees...</div>
                    ) : error ? (
                        <div className="teams-message error">{error}</div>
                    ) : (
                        <table className="teams-table">
                            <colgroup>
                                <col />{/* Full Name   */}
                                <col />{/* Job Title   */}
                                <col />{/* Department  */}
                                <col />{/* Role        */}
                                <col />{/* Status      */}
                                <col />{/* Action      */}
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>Full Name</th>
                                    <th>Job Title</th>
                                    <th>Department</th>
                                    <th>Role</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredEmployees.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="teams-empty">
                                            No employees match your search or filters.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredEmployees.map((employee) => (
                                        <tr key={employee.id}>
                                            <td>
                                                {(() => {
                                                    const full = [employee.firstName, employee.middleName, employee.lastName]
                                                        .filter(Boolean).join(' ') || '—';
                                                    return <span className="cell-tooltip" data-tooltip={full}>{full}</span>;
                                                })()}
                                            </td>
                                            <td><span className="cell-tooltip" data-tooltip={employee.jobTitle || '—'}>{employee.jobTitle || '—'}</span></td>
                                            <td><span className="cell-tooltip" data-tooltip={employee.department || '—'}>{employee.department || '—'}</span></td>
                                            <td>
                                                <span className={`teams-role-badge teams-role--${(employee.role || '').toLowerCase()}`}>
                                                    {employee.role || '—'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`teams-status-badge ${employee.isActive ? 'teams-status--active' : 'teams-status--inactive'}`}>
                                                    {employee.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="action-btns">
                                                    <button className="edit-btn" title="Edit" onClick={() => handleEditClick(employee)}>
                                                        <FiEdit2 size={14} />
                                                    </button>
                                                    <button className="delete-btn" title="Delete" onClick={() => handleDeleteClick(employee)}>
                                                        <FiTrash2 size={14} />
                                                    </button>
                                                    <button
                                                        className="view-btn"
                                                        title="View Full Profile"
                                                        onClick={() => navigate(`/hr/employee/${employee.employeeId}`, { state: { employee } })}
                                                    >
                                                        <FiEye />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* results count */}
                {!loading && !error && (
                    <div className="teams-results-count">
                        Showing {filteredEmployees.length} of {employees.length} employee{employees.length !== 1 ? 's' : ''}
                    </div>
                )}

            </div>

            {/* ════════════════════════════════════════
                FILTER PANEL (slide-in from right)
            ════════════════════════════════════════ */}
            {showFilterPanel && (
                <div className="filter-overlay" onClick={() => setShowFilterPanel(false)}>
                    <div className="filter-panel" onClick={e => e.stopPropagation()}>

                        <div className="filter-panel-header">
                            <span className="filter-panel-title">
                                <FiFilter size={16} /> Filters
                            </span>
                            <button
                                className="filter-panel-close"
                                onClick={() => setShowFilterPanel(false)}
                            >
                                <FiX size={18} />
                            </button>
                        </div>

                        <div className="filter-panel-body">

                            {/* Role */}
                            <div className="filter-field">
                                <label>Role</label>
                                <select
                                    name="role"
                                    value={pendingFilters.role}
                                    onChange={handlePendingFilterChange}
                                >
                                    <option value="">All Roles</option>
                                    {uniqueRoles.map(r => (
                                        <option key={r} value={r}>{r}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Status */}
                            <div className="filter-field">
                                <label>Status</label>
                                <select
                                    name="status"
                                    value={pendingFilters.status}
                                    onChange={handlePendingFilterChange}
                                >
                                    <option value="">All Statuses</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>

                            {/* Department */}
                            <div className="filter-field">
                                <label>Department</label>
                                <select
                                    name="department"
                                    value={pendingFilters.department}
                                    onChange={handlePendingFilterChange}
                                >
                                    <option value="">All Departments</option>
                                    {uniqueDepartments.map(d => (
                                        <option key={d} value={d}>{d}</option>
                                    ))}
                                </select>
                            </div>

                        </div>

                        <div className="filter-panel-footer">
                            <button className="filter-clear-btn" onClick={clearFilters}>
                                Clear All
                            </button>
                            <button className="filter-apply-btn" onClick={applyFilters}>
                                Apply Filters
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {/* ════════════ REGISTER MODAL ════════════ */}
            {showRegisterModal && (
                <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center p-4 sm:p-6 overflow-y-auto animate-fade-in">
                    <div className="bg-white w-full max-w-4xl rounded-2xl shadow-xl flex flex-col relative max-h-[90vh] my-auto animate-slide-up overflow-hidden">
                        <div className="flex justify-between items-center px-8 py-5 border-b border-slate-100 bg-slate-50/50 sticky top-0 z-10">
                            <h2 className="text-xl font-bold text-slate-800 tracking-tight">Register Employee</h2>
                            <button className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors" onClick={() => setShowRegisterModal(false)}>×</button>
                        </div>

                        <div className="px-8 py-6 border-b border-slate-100 last:border-0">
                            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-6 flex items-center gap-2 before:content-[''] before:w-1.5 before:h-4 before:bg-blue-600 before:rounded-full">Basic Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                                <div className="flex flex-col gap-1.5"><label>Username</label><input type="text" name="username" placeholder='Enter Username' value={formData.username} onChange={handleInputChange} /></div>
                                <div className="flex flex-col gap-1.5">
                                    <label>Password</label>
                                    <div className="password-input-wrapper">
                                        <input type={showPassword ? 'text' : 'password'} name="password" placeholder='Enter password' value={formData.password} onChange={handleInputChange} />
                                        <button type="button" className="password-toggle-btn" onClick={() => setShowPassword(!showPassword)}>
                                            {showPassword ? <FiEyeOff /> : <FiEye />}
                                        </button>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1.5"><label>Email</label><input type="email" name="email" placeholder='company@orakris.com' value={formData.email} onChange={handleInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Role</label><select name="role" value={formData.role} onChange={handleInputChange}><option value="USER">USER</option><option value="ADMIN">ADMIN</option><option value="SUPERADMIN">SUPERADMIN</option><option value="TESTER">TESTER</option></select></div>
                                <div className="flex flex-col gap-1.5"><label>Employee ID</label><input type="text" name="employeeId" placeholder='EMP001' value={formData.employeeId} onChange={handleInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Attendance ID</label><input type="text" name="attendanceId" placeholder='101' value={formData.attendanceId} onChange={handleInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>First Name</label><input type="text" name="firstName" placeholder='Enter First Name' value={formData.firstName} onChange={handleInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Middle Name</label><input type="text" name="middleName" placeholder='Enter Middle Name' value={formData.middleName} onChange={handleInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Last Name</label><input type="text" name="lastName" placeholder='Enter Last Name' value={formData.lastName} onChange={handleInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Date Of Birth</label><input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Gender</label><select name="gender" value={formData.gender} onChange={handleInputChange}><option value="MALE">MALE</option><option value="FEMALE">FEMALE</option></select></div>
                                <div className="flex flex-col gap-1.5"><label>Phone Number</label><input type="text" name="phoneNumber" placeholder='Enter Phone Number' value={formData.phoneNumber} onChange={handleInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Alternate Phone</label><input type="text" name="alternatePhone" placeholder='Enter Alternate Phone' value={formData.alternatePhone} onChange={handleInputChange} /></div>
                            </div>
                        </div>

                        <div className="px-8 py-6 border-b border-slate-100 last:border-0">
                            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-6 flex items-center gap-2 before:content-[''] before:w-1.5 before:h-4 before:bg-blue-600 before:rounded-full">Job Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                                <div className="flex flex-col gap-1.5"><label>Department</label><input type="text" name="department" placeholder='Enter Department' value={formData.department} onChange={handleInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Job Title</label><input type="text" name="jobTitle" placeholder='Enter Job Title' value={formData.jobTitle} onChange={handleInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Hire Date</label><input type="date" name="hireDate" value={formData.hireDate} onChange={handleInputChange} /></div>
                                <div className="flex flex-col gap-1.5">
                                    <label>Manager <span className="form-optional">(direct report)</span></label>
                                    <select
                                        name="managerId"
                                        value={formData.managerId || ''}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">— No Manager —</option>
                                        {employees
                                            .sort((a, b) => [a.firstName, a.lastName].join(' ').localeCompare([b.firstName, b.lastName].join(' ')))
                                            .map(e => (
                                                <option key={e.id} value={e.id}>
                                                    {[e.firstName, e.lastName].filter(Boolean).join(' ') || e.username} ({e.employeeId})
                                                </option>
                                            ))}
                                    </select>
                                </div>
                                <div className="flex flex-col gap-1.5"><label>Employment Type</label><select name="employmentType" value={formData.employmentType} onChange={handleInputChange}><option value="FULL_TIME">FULL_TIME</option><option value="PART_TIME">PART_TIME</option><option value="CONTRACT">CONTRACT</option></select></div>
                                <div className="flex flex-col gap-1.5"><label>Work Location</label><input type="text" name="workLocation" placeholder='Enter Work Location' value={formData.workLocation} onChange={handleInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Salary</label><input type="number" name="salary" placeholder='Enter Salary' value={formData.salary} onChange={handleInputChange} /></div>
                            </div>
                        </div>

                        <div className="px-8 py-6 border-b border-slate-100 last:border-0">
                            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-6 flex items-center gap-2 before:content-[''] before:w-1.5 before:h-4 before:bg-blue-600 before:rounded-full">Address Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                                <div className="flex flex-col gap-1.5"><label>Address Line 1</label><input type="text" name="addressLine1" placeholder='Enter Address Line 1' value={formData.addressLine1} onChange={handleInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Address Line 2</label><input type="text" name="addressLine2" placeholder='Enter Address Line 2' value={formData.addressLine2} onChange={handleInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>City</label><input type="text" name="city" placeholder='Enter City' value={formData.city} onChange={handleInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>State</label><input type="text" name="state" placeholder='Enter State' value={formData.state} onChange={handleInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Postal Code</label><input type="text" name="postalCode" placeholder='Enter Postal Code' value={formData.postalCode} onChange={handleInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Country</label><input type="text" name="country" placeholder='Enter Country' value={formData.country} onChange={handleInputChange} /></div>
                            </div>
                        </div>

                        <div className="px-8 py-6 border-b border-slate-100 last:border-0">
                            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-6 flex items-center gap-2 before:content-[''] before:w-1.5 before:h-4 before:bg-blue-600 before:rounded-full">Banking Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                                <div className="flex flex-col gap-1.5"><label>Bank Account Number</label><input type="text" name="bankAccountNumber" placeholder='Enter Bank Account Number' value={formData.bankAccountNumber} onChange={handleInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Bank Name</label><input type="text" name="bankName" placeholder='Enter Bank Name' value={formData.bankName} onChange={handleInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>IFSC Code</label><input type="text" name="ifscCode" placeholder='Enter IFSC Code' value={formData.ifscCode} onChange={handleInputChange} /></div>
                            </div>
                        </div>

                        <div className="px-8 py-6 border-b border-slate-100 last:border-0">
                            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-6 flex items-center gap-2 before:content-[''] before:w-1.5 before:h-4 before:bg-blue-600 before:rounded-full">Emergency Contact</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                                <div className="flex flex-col gap-1.5"><label>Emergency Contact Name</label><input type="text" name="emergencyContactName" placeholder='Enter Emergency Contact Name' value={formData.emergencyContactName} onChange={handleInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Emergency Contact Phone</label><input type="text" name="emergencyContactPhone" placeholder='Enter Emergency Contact Phone' value={formData.emergencyContactPhone} onChange={handleInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Relationship</label><input type="text" name="emergencyContactRelationship" placeholder='Enter Relationship' value={formData.emergencyContactRelationship} onChange={handleInputChange} /></div>
                            </div>
                        </div>

                        <div className="px-8 py-5 border-t border-slate-100 bg-slate-50/80 flex justify-end items-center gap-3 sticky bottom-0 z-10">
                            <button className="px-6 py-2.5 rounded-lg text-sm font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors" onClick={() => setShowRegisterModal(false)}>Cancel</button>
                            <button className="px-6 py-2.5 rounded-lg text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-sm shadow-blue-200 transition-all active:scale-95" onClick={handleRegister}>Register Employee</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ════════════ DELETE CONFIRMATION MODAL ════════════ */}
            {deleteTarget && (
                <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center p-4 sm:p-6 overflow-y-auto animate-fade-in">
                    <div className="bg-white w-full max-w-md rounded-2xl shadow-xl flex flex-col p-8 text-center animate-slide-up">
                        <div className="confirm-icon"><FiTrash2 size={28} /></div>
                        <h3 className="confirm-title">Delete Employee</h3>
                        <p className="confirm-message">
                            Are you sure you want to deactivate{' '}
                            <strong>{[deleteTarget.firstName, deleteTarget.lastName].filter(Boolean).join(' ') || deleteTarget.employeeId}</strong>?
                            <br /><span className="confirm-sub">This action will deactivate the account.</span>
                        </p>
                        <div className="confirm-actions">
                            <button className="px-6 py-2.5 rounded-lg text-sm font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors" onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>Cancel</button>
                            <button className="confirm-delete-btn" onClick={handleDeleteConfirm} disabled={deleteLoading}>
                                {deleteLoading ? 'Deleting…' : 'Yes, Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ════════════ EDIT MODAL ════════════ */}
            {showEditModal && editEmployee && (
                <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center p-4 sm:p-6 overflow-y-auto animate-fade-in">
                    <div className="bg-white w-full max-w-4xl rounded-2xl shadow-xl flex flex-col relative max-h-[90vh] my-auto animate-slide-up overflow-hidden">
                        <div className="flex justify-between items-center px-8 py-5 border-b border-slate-100 bg-slate-50/50 sticky top-0 z-10">
                            <h2 className="text-xl font-bold text-slate-800 tracking-tight">Edit Employee — {editEmployee.employeeId}</h2>
                            <button className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors" onClick={() => setShowEditModal(false)}>×</button>
                        </div>

                        <div className="px-8 py-6 border-b border-slate-100 last:border-0">
                            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-6 flex items-center gap-2 before:content-[''] before:w-1.5 before:h-4 before:bg-blue-600 before:rounded-full">Basic Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                                <div className="flex flex-col gap-1.5"><label>Username</label><input type="text" name="username" value={editFormData.username} onChange={handleEditInputChange} /></div>
                                <div className="flex flex-col gap-1.5">
                                    <label>New Password <span className="form-optional">(leave blank to keep current)</span></label>
                                    <div className="password-input-wrapper">
                                        <input type={showEditPassword ? 'text' : 'password'} name="password" placeholder="Enter new password" value={editFormData.password} onChange={handleEditInputChange} />
                                        <button type="button" className="password-toggle-btn" onClick={() => setShowEditPassword(!showEditPassword)}>
                                            {showEditPassword ? <FiEyeOff /> : <FiEye />}
                                        </button>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1.5"><label>Email</label><input type="email" name="email" value={editFormData.email} onChange={handleEditInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Role</label><select name="role" value={editFormData.role} onChange={handleEditInputChange}><option value="USER">USER</option><option value="ADMIN">ADMIN</option><option value="SUPERADMIN">SUPERADMIN</option><option value="TESTER">TESTER</option></select></div>
                                <div className="flex flex-col gap-1.5"><label>Attendance ID</label><input type="text" name="attendanceId" placeholder='101' value={editFormData.attendanceId} onChange={handleEditInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>First Name</label><input type="text" name="firstName" value={editFormData.firstName} onChange={handleEditInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Middle Name</label><input type="text" name="middleName" value={editFormData.middleName} onChange={handleEditInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Last Name</label><input type="text" name="lastName" value={editFormData.lastName} onChange={handleEditInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Date of Birth</label><input type="date" name="dateOfBirth" value={editFormData.dateOfBirth} onChange={handleEditInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Gender</label><select name="gender" value={editFormData.gender} onChange={handleEditInputChange}><option value="MALE">MALE</option><option value="FEMALE">FEMALE</option><option value="OTHER">OTHER</option></select></div>
                                <div className="flex flex-col gap-1.5"><label>Phone Number</label><input type="text" name="phoneNumber" value={editFormData.phoneNumber} onChange={handleEditInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Alternate Phone</label><input type="text" name="alternatePhone" value={editFormData.alternatePhone} onChange={handleEditInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Active</label><select name="isActive" value={editFormData.isActive ? 'true' : 'false'} onChange={e => setEditFormData(prev => ({ ...prev, isActive: e.target.value === 'true' }))}><option value="true">Active</option><option value="false">Inactive</option></select></div>
                            </div>
                        </div>

                        <div className="px-8 py-6 border-b border-slate-100 last:border-0">
                            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-6 flex items-center gap-2 before:content-[''] before:w-1.5 before:h-4 before:bg-blue-600 before:rounded-full">Job Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                                <div className="flex flex-col gap-1.5"><label>Job Title</label><input type="text" name="jobTitle" value={editFormData.jobTitle} onChange={handleEditInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Department</label><input type="text" name="department" value={editFormData.department} onChange={handleEditInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Hire Date</label><input type="date" name="hireDate" value={editFormData.hireDate} onChange={handleEditInputChange} /></div>
                                <div className="flex flex-col gap-1.5">
                                    <label>Manager <span className="form-optional">(direct report)</span></label>
                                    <select
                                        name="managerId"
                                        value={editFormData.managerId || ''}
                                        onChange={handleEditInputChange}
                                    >
                                        <option value="">— No Manager —</option>
                                        {employees
                                            .filter(e => e.id !== editEmployee?.id)
                                            .sort((a, b) => [a.firstName, a.lastName].join(' ').localeCompare([b.firstName, b.lastName].join(' ')))
                                            .map(e => (
                                                <option key={e.id} value={e.id}>
                                                    {[e.firstName, e.lastName].filter(Boolean).join(' ') || e.username} ({e.employeeId})
                                                </option>
                                            ))}
                                    </select>
                                </div>
                                <div className="flex flex-col gap-1.5"><label>Employment Type</label><select name="employmentType" value={editFormData.employmentType} onChange={handleEditInputChange}><option value="FULL_TIME">FULL_TIME</option><option value="PART_TIME">PART_TIME</option><option value="CONTRACT">CONTRACT</option><option value="INTERN">INTERN</option></select></div>
                                <div className="flex flex-col gap-1.5"><label>Work Location</label><input type="text" name="workLocation" value={editFormData.workLocation} onChange={handleEditInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Salary (₹)</label><input type="number" name="salary" value={editFormData.salary} onChange={handleEditInputChange} /></div>
                            </div>
                        </div>

                        <div className="px-8 py-6 border-b border-slate-100 last:border-0">
                            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-6 flex items-center gap-2 before:content-[''] before:w-1.5 before:h-4 before:bg-blue-600 before:rounded-full">Address Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                                <div className="flex flex-col gap-1.5"><label>Address Line 1</label><input type="text" name="addressLine1" value={editFormData.addressLine1} onChange={handleEditInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Address Line 2</label><input type="text" name="addressLine2" value={editFormData.addressLine2} onChange={handleEditInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>City</label><input type="text" name="city" value={editFormData.city} onChange={handleEditInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>State</label><input type="text" name="state" value={editFormData.state} onChange={handleEditInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Postal Code</label><input type="text" name="postalCode" value={editFormData.postalCode} onChange={handleEditInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Country</label><input type="text" name="country" value={editFormData.country} onChange={handleEditInputChange} /></div>
                            </div>
                        </div>

                        <div className="px-8 py-6 border-b border-slate-100 last:border-0">
                            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-6 flex items-center gap-2 before:content-[''] before:w-1.5 before:h-4 before:bg-blue-600 before:rounded-full">Banking Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                                <div className="flex flex-col gap-1.5"><label>Bank Name</label><input type="text" name="bankName" value={editFormData.bankName} onChange={handleEditInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Account Number</label><input type="text" name="bankAccountNumber" value={editFormData.bankAccountNumber} onChange={handleEditInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>IFSC Code</label><input type="text" name="ifscCode" value={editFormData.ifscCode} onChange={handleEditInputChange} /></div>
                            </div>
                        </div>

                        <div className="px-8 py-6 border-b border-slate-100 last:border-0">
                            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-6 flex items-center gap-2 before:content-[''] before:w-1.5 before:h-4 before:bg-blue-600 before:rounded-full">Emergency Contact</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                                <div className="flex flex-col gap-1.5"><label>Contact Name</label><input type="text" name="emergencyContactName" value={editFormData.emergencyContactName} onChange={handleEditInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Contact Phone</label><input type="text" name="emergencyContactPhone" value={editFormData.emergencyContactPhone} onChange={handleEditInputChange} /></div>
                                <div className="flex flex-col gap-1.5"><label>Relationship</label><input type="text" name="emergencyContactRelationship" value={editFormData.emergencyContactRelationship} onChange={handleEditInputChange} /></div>
                            </div>
                        </div>

                        <div className="px-8 py-5 border-t border-slate-100 bg-slate-50/80 flex justify-end items-center gap-3 sticky bottom-0 z-10">
                            <button className="px-6 py-2.5 rounded-lg text-sm font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors" onClick={() => setShowEditModal(false)} disabled={editSaving}>Cancel</button>
                            <button className="px-6 py-2.5 rounded-lg text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-sm shadow-blue-200 transition-all active:scale-95" onClick={handleUpdate} disabled={editSaving}>
                                {editSaving ? 'Saving…' : 'Update Employee'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ════════════ PROFILE MODAL ════════════ */}
            {showProfileModal && selectedEmployee && (
                <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center p-4 sm:p-6 overflow-y-auto animate-fade-in">
                    <div className="bg-white w-full max-w-3xl rounded-2xl shadow-xl flex flex-col overflow-hidden max-h-[90vh] animate-slide-up">
                        <div className="flex justify-between items-center px-8 py-5 border-b border-slate-100 bg-slate-50/50 sticky top-0 z-10">
                            <h2 className="text-xl font-bold text-slate-800 tracking-tight">Employee Profile</h2>
                            <button className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors" onClick={() => setShowProfileModal(false)}>×</button>
                        </div>
                        <div className="p-8 bg-gradient-to-br from-slate-900 to-slate-800 text-white flex items-center gap-6">
                            <div className="w-24 h-24 rounded-2xl bg-white/10 border-2 border-white/20 flex items-center justify-center text-4xl font-bold shadow-inner">{selectedEmployee.firstName?.charAt(0)}</div>
                            <div>
                                <h3>{[selectedEmployee.firstName, selectedEmployee.middleName, selectedEmployee.lastName].filter(Boolean).join(' ')}</h3>
                                <p>{selectedEmployee.jobTitle || 'Employee'}</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 p-8 bg-white">
                            <div><strong>Employee ID</strong><p>{selectedEmployee.employeeId || '—'}</p></div>
                            <div><strong>Email</strong><p>{selectedEmployee.email || '—'}</p></div>
                            <div><strong>Phone Number</strong><p>{selectedEmployee.phoneNumber || '—'}</p></div>
                            <div><strong>Department</strong><p>{selectedEmployee.department || '—'}</p></div>
                            <div><strong>Role</strong><p>{selectedEmployee.role || '—'}</p></div>
                            <div><strong>Work Location</strong><p>{selectedEmployee.workLocation || '—'}</p></div>
                            <div><strong>Hire Date</strong><p>{selectedEmployee.hireDate || '—'}</p></div>
                            <div><strong>Employment Type</strong><p>{selectedEmployee.employmentType || '—'}</p></div>
                        </div>
                        <div className="p-8 bg-slate-50 border-t border-slate-100">
                            <h3>Certificates</h3>
                            {employeeCertificates.length === 0 ? (
                                <p className="text-sm text-slate-500 italic">No certificates uploaded.</p>
                            ) : (
                                employeeCertificates.map((cert) => (
                                    <div key={cert.id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-center gap-4 mb-3 hover:border-blue-300 transition-colors">
                                        <div><h4>{cert.certificateName}</h4><p>{cert.certificateType}</p></div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default HrTeams;

